import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Grid,
  Switch,
  FormControlLabel,
  Autocomplete,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { toast } from "react-toastify";
import api from "../../conf/api";

/**
 * Subbrands Management Page
 * CRUD operations for subbrands
 */
function SubbrandsPreview() {
  const [subbrands, setSubbrands] = useState([]);
  const [brands, setBrands] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSubbrand, setEditingSubbrand] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    brand: null,
    description: "",
    language: null,
    disabled: false,
  });

  useEffect(() => {
    fetchSubbrands();
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchBrands();
    fetchLanguages();
  }, []);

  const fetchSubbrands = async () => {
    try {
      setLoading(true);
      const response = await api.get("/mrss/subbrands/", {
        params: {
          page: paginationModel.page + 1,
          page_size: paginationModel.pageSize,
        },
      });
      setSubbrands(response.data.results || response.data);
      setRowCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching subbrands:", error);
      toast.error("Failed to load subbrands");
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get("/mrss/brands/?page_size=1000");
      setBrands(response.data.results || response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
    }
  };

  const fetchLanguages = async () => {
    try {
      const response = await api.get("/youtube/languages/");
      setLanguages(response.data);
    } catch (error) {
      console.error("Error fetching languages:", error);
    }
  };

  const handleOpenDialog = (subbrand = null) => {
    if (subbrand) {
      setEditingSubbrand(subbrand);
      setFormData({
        name: subbrand.name,
        brand: subbrand.brand,
        description: subbrand.description || "",
        language: subbrand.language?.id || null,
        disabled: subbrand.disabled,
      });
    } else {
      setEditingSubbrand(null);
      setFormData({
        name: "",
        brand: null,
        description: "",
        language: null,
        disabled: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSubbrand(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        toast.warning("Subbrand name is required");
        return;
      }

      if (!formData.brand) {
        toast.warning("Brand is required");
        return;
      }

      if (editingSubbrand) {
        await api.put(`/mrss/subbrands/${editingSubbrand.id}/`, formData);
        toast.success("Subbrand updated successfully");
      } else {
        await api.post("/mrss/subbrands/", formData);
        toast.success("Subbrand created successfully");
      }

      handleCloseDialog();
      fetchSubbrands();
    } catch (error) {
      console.error("Error saving subbrand:", error);
      toast.error(error.response?.data?.error || "Failed to save subbrand");
    }
  };

  const handleDelete = async (subbrandId) => {
    if (window.confirm(`Are you sure you want to delete this subbrand?`)) {
      try {
        await api.delete(`/mrss/subbrands/${subbrandId}/`);
        toast.success("Subbrand deleted successfully");
        fetchSubbrands();
      } catch (error) {
        console.error("Error deleting subbrand:", error);
        toast.error("Failed to delete subbrand");
      }
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Subbrand Name",
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <AccountTreeIcon color="secondary" fontSize="small" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "brand_name",
      headerName: "Parent Brand",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: "language",
      headerName: "Language",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value?.name || "Use Brand's"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "feeds_count",
      headerName: "Feeds",
      flex: 0.5,
      align: "center",
      renderCell: (params) => (
        <Chip label={params.value} color="primary" size="small" />
      ),
    },
    {
      field: "disabled",
      headerName: "Status",
      flex: 0.6,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Disabled" : "Active"}
          color={params.value ? "error" : "success"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Subbrand Management
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage subbrands under parent brands
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Subbrand
        </Button>
      </Box>

      <Paper sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={subbrands}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={rowCount}
          disableSelectionOnClick
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSubbrand ? "Edit Subbrand" : "Create New Subbrand"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subbrand Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={brands}
                getOptionLabel={(option) => option.name}
                value={brands.find((b) => b.name === formData.brand) || null}
                onChange={(e, newValue) =>
                  setFormData({ ...formData, brand: newValue?.name || null })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Parent Brand" required />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={languages}
                getOptionLabel={(option) => option.name}
                value={languages.find((l) => l.id === formData.language) || null}
                onChange={(e, newValue) =>
                  setFormData({ ...formData, language: newValue?.id || null })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Language (Optional)"
                    helperText="Leave empty to use parent brand's language"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.disabled}
                    onChange={(e) =>
                      setFormData({ ...formData, disabled: e.target.checked })
                    }
                  />
                }
                label="Disabled"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingSubbrand ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SubbrandsPreview;
